import { Button, Card } from '@cloudclevr/components';
import { ChartData } from 'chart.js';
import DoughnutChart from '@components/ui/Charts/DoughnutChart';
import { Loader } from '@cloudclevr/components';
import { ArrowDownIcon, HamburgerIcon } from '@cloudclevr/components';
import styles from './styles.module.css';
import cn from 'classnames';
import { SelectDropdown, SelectItem } from '@cloudclevr/components';
import { DropdownMenu, MenuItem } from '@cloudclevr/components';
import { useState } from 'react';
import { DEFAULT_CHART_COLORS } from '@common/constants';
import { TileFooter } from '../partials/TileFooter';

export type DoughnutChartTileProps = {
  sortData?: MenuItem[];
  compareData?: MenuItem[];
  contextData: MenuItem[];
  headerTitle: string;
  isLoading: boolean;
  data: number[];
  dataLabels: string[];
  dataLabelValues?: string[];
  handleDropdownSelect?: (value: any) => void;
  buttonLabel?: string;
  handleButtonClick?: () => void;
  classNames?: string;
  isLgSize?: boolean;
  checkBoxItems?: SelectItem[];
  dropdownMenuItemClasses?: string;
  initialSelectedItem?: MenuItem;
  selectedItemsIds?: string[];
  showBracketLabelValue?: boolean;
  initialCompareSelectedItem?: MenuItem;
  animation?: boolean;
  colors?: string[];
  isConsistentDropdownMenuItemClasses?: boolean;
  additionalTextInfo?: React.ReactNode;
  isPrintView?: boolean;
  linkPath?: string;
  showMessageIfEmpty?: boolean;
  emptyMessageTextAndSubtext?: EmptyMessageTextAndSubtext;
  'data-testid'?: string;
  buttonTestId?: string;
};

export const DoughnutChartTile: React.FC<DoughnutChartTileProps> = ({
  sortData,
  compareData,
  headerTitle,
  isLoading = false,
  data = [0, 0],
  dataLabels,
  dataLabelValues,
  contextData,
  handleDropdownSelect,
  buttonLabel,
  handleButtonClick,
  classNames,
  isLgSize = false,
  dropdownMenuItemClasses,
  initialSelectedItem,
  checkBoxItems,
  selectedItemsIds,
  animation = true,
  showBracketLabelValue = true,
  initialCompareSelectedItem,
  colors,
  isConsistentDropdownMenuItemClasses,
  additionalTextInfo,
  isPrintView,
  linkPath,
  showMessageIfEmpty = true,
  emptyMessageTextAndSubtext = null,
  'data-testid': dataTestId = null,
  buttonTestId,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const chartData: ChartData<'doughnut'> = {
    datasets: [
      {
        data,
        backgroundColor: colors ?? DEFAULT_CHART_COLORS,
      },
    ],
  };

  const getChartLegendDotColor = (index: number) => {
    const chartColors = colors ?? DEFAULT_CHART_COLORS;
    const colorIndex = chartColors.length;
    return chartColors[index % colorIndex];
  };

  const handleChange = (id: string) => {
    handleDropdownSelect && handleDropdownSelect(id);
  }

  return (
    <Card classNames={`!w-auto ${classNames}`} data-testid={dataTestId}>
      <Card.Header classNames={styles.cardHeader} data-testid={`${dataTestId}-header`}>
        <div className={styles.cardHeaderContainer}>
          <span className="mb-2" data-testid="doughnut-chart-tile-header">{headerTitle}</span>
          {contextData.length ? (
            <DropdownMenu
              items={contextData}
              buttonProps={{ icon: <HamburgerIcon></HamburgerIcon> }}
              dropdownMenuClasses={dropdownMenuItemClasses}
              data-testid={`${dataTestId}-context-dropdown`}
            ></DropdownMenu>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          {!!sortData?.length && (
            <DropdownMenu
              items={sortData}
              showSelectedItem={true}
              dropdownMenuClasses={
                isConsistentDropdownMenuItemClasses
                  ? dropdownMenuItemClasses
                  : undefined
              }
              buttonProps={{
                label: sortData[0].label,
              }}
              initialSelectedItem={initialSelectedItem || sortData[0]}
              data-testid = {`${dataTestId}-sort-dropdown`}
            ></DropdownMenu>
          )}
          {compareData && (
            <DropdownMenu
              items={compareData}
              showSelectedItem={true}
              buttonProps={{
                label: compareData[0].label,
              }}
              dropdownMenuClasses={'!w-[220px]'}
              initialSelectedItem={initialCompareSelectedItem || compareData[0]}
              data-testid = {`${dataTestId}-compare-dropdown`}
            ></DropdownMenu>
          )}

          {checkBoxItems && (
            <SelectDropdown
              header={
                <Button
                  testId='device-type-button'
                  onClick={() => {
                    setIsDropdownOpen(!isDropdownOpen);
                  }}
                  size="small"
                  type="button"
                  variant="inactiveOutline"
                  className="mt-3 w-auto border-none !bg-interfaceColor-15 !shadow-none"
                >
                  Device type <ArrowDownIcon size="3" classNames="ml-1" />
                </Button>
              }
              isSingleSelect={false}
              classNames={'!w-[300px]'}
              isOpen={isDropdownOpen}
              setIsOpen={setIsDropdownOpen}
              items={checkBoxItems}
              onSelect={(ids: string[], selectedItem) => {
                if (selectedItem)
                  handleChange(selectedItem?.value)
              }}
              data-testid = {`${dataTestId}-device-type-dropdown`}

            />
          )}
        </div>
      </Card.Header>

      <Card.Content classNames={styles.cardContent} data-testid={`${dataTestId}-content`}>
        {isLoading ? (
          <div className="flex w-full justify-center">
            <Loader />
          </div>
        ) : (
          <div className={styles.cardDetails}>
            <div
              className={cn(
                styles.chartContainer,
                isLgSize ? 'lg:!h-[14rem]' : ''
              )}
            >
              {chartData.datasets[0].data.some((i) => i > 0) || !showMessageIfEmpty ? (
                <>
                  <div className="w-1/2 md:w-auto">
                    <DoughnutChart
                      classNames={cn(
                        styles.chart,
                        isLgSize ? 'lg:!h-[14rem] lg:!w-[14rem]' : ''
                      )}
                      data={chartData}
                      animation={animation}
                      data-testid = {`${dataTestId}-chart`}
                    />
                  </div>
                  <div className="max-h-40 w-1/2 overflow-y-auto md:w-auto" data-testid = {`${dataTestId}-legend`}>
                    {isPrintView &&
                      dataLabels.map((label, index) => (
                        <div key={index} className={styles.chartLegendItem}>
                          <svg
                            className={styles.chartLegendDot}
                            viewBox="0 0 12 12"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <circle
                              cx="6"
                              cy="6"
                              r="6"
                              fill={getChartLegendDotColor(index)}
                            />
                          </svg>
                          <span className="text-xs">{label}</span>
                        </div>
                      ))}
                    {!isPrintView &&
                      dataLabels.map((label, index) => (
                        <div key={index} className={styles.chartLegendItem}>
                          <svg
                            className={styles.chartLegendDot}
                            viewBox="0 0 12 12"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <circle
                              cx="6"
                              cy="6"
                              r="6"
                              fill={getChartLegendDotColor(index)}
                            />
                          </svg>
                          <span>
                            {label}
                            {showBracketLabelValue && (
                              <span className="ml-1 break-words">
                                (
                                {dataLabelValues && dataLabelValues[index]
                                  ? dataLabelValues[index]
                                  : data && data[index]}
                                )
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    {additionalTextInfo ?? additionalTextInfo}
                  </div>
                </>
              ) : (
                <>
                  {emptyMessageTextAndSubtext != null ? (
                    <div className="my-3 flex w-full flex-col items-center justify-center">
                    <h1 className="mb-2 rounded-full bg-interfaceColor-20 px-4 py-1 text-20 text-interfaceColor-100" data-testid={`${dataTestId}-empty-message`}>
                      {emptyMessageTextAndSubtext.Text}
                    </h1>
                    <p className="max-w-sm text-center text-interfaceColor-80">
                      {emptyMessageTextAndSubtext.SubText}
                    </p>
                  </div>
                  ) : (
                    <div className="my-3 flex w-full flex-col items-center justify-center" data-testid={`${dataTestId}-empty-message`}>
                      <h1 className="mb-2 rounded-full bg-interfaceColor-20 px-4 py-1 text-20 text-interfaceColor-100">
                        Insufficient data
                      </h1>
                      <p className="max-w-sm text-center text-interfaceColor-80">
                        Keep using Clevr360 to see how your
                        <span className="mx-1">{headerTitle}</span>
                        have changed over time
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card.Content>
      <TileFooter
        linkPath={linkPath}
        buttonLabel={buttonLabel}
        handleButtonClick={handleButtonClick}
        testId={buttonTestId}
        data-testid={`${dataTestId}-footer`} 
      />
    </Card>
  );
};

export interface EmptyMessageTextAndSubtext {
  Text: string;
  SubText?: string;
}